import classNames from 'classnames'
import { type ReactNode, type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { Button, type IButtonProps, Icon, Status, Typography } from 'shared/ui'
import type { IStatusProps } from 'shared/ui/Status'
import { RegisterSectionItem } from './RegisterSectionItem'
import styles from './styles.module.scss'

export type IRegisterSectionProps = {
  title: ReactNode
  label?: string
  desc: ReactNode
  learnMoreLink?: string
  descContentClassName?: string
  isSecondStep?: boolean

  statusProps?: IStatusProps | null
  type?: 'default' | 'transparent'
  section?: 'default' | 'gray' | 'border'
  titleType?: 'body-md-medium' | 'body-lg-medium'
  containerClassName?: string
  actionButton?: IButtonProps
  actionIcon?: {
    onClick: () => void
  }

  items?: {
    id: number
    title: string
    desc: string
    statusProps?: IStatusProps | null
    actionIcon?: {
      onClick: (id: number) => void
    }
  }[]
}

export const RegisterSection: FC<IRegisterSectionProps> = observer(
  ({
    title,
    label,
    desc,
    type = 'default',
    section = 'default',
    titleType = 'body-md-medium',
    containerClassName,
    learnMoreLink,
    isSecondStep,
    statusProps,
    descContentClassName = '',
    actionButton,
    actionIcon,
    items,
  }) => {
    const isBothButtons = actionButton && actionIcon

    return (
      <div
        className={classNames(
          styles.wrap,
          styles[`type-${type}`],
          styles[`section-${section}`],
          {
            [styles.secondStep]: isSecondStep,
            [styles.clickable]: actionIcon?.onClick,
          },
          containerClassName
        )}
        onClick={actionIcon?.onClick}
      >
        <div
          className={classNames(styles.wrap__sub, {
            [styles.wrap__sub_bothButtons]: isBothButtons,
          })}
        >
          <div className={styles.content}>
            {label && (
              <Status
                className={styles.label}
                title={'Add-on'}
                intent={'purple'}
                emphasis={'high'}
                size={'small'}
              />
            )}

            <Typography ariaLabel='RegisterSection_title' variant={titleType}>
              {title}
            </Typography>
            <Typography
              ariaLabel='RegisterSection_desc'
              variant='body-md-regular'
              color='var(--content-primary-tertiary)'
              className={styles.desc}
              tag='div'
            >
              <div className={classNames(styles.descContent, descContentClassName)}>
                {desc}
                {learnMoreLink && (
                  <a
                    href={learnMoreLink}
                    target='_blank'
                    rel='noreferrer'
                    className={classNames(styles.learnMoreLink)}
                  >
                    <Typography
                      ariaLabel='TermsAndPrivacy_link'
                      variant='body-md-medium'
                      className={styles.learnMoreText}
                    >
                      Learn more
                    </Typography>
                  </a>
                )}
              </div>
            </Typography>
            {statusProps && (
              <div className={styles.status}>
                <Status {...statusProps} />
              </div>
            )}
          </div>

          {actionIcon && (
            <div>
              <div
                className={classNames(styles.arrowRight, {
                  [styles.arrowRight_bothButtons]: isBothButtons,
                })}
              >
                {actionButton && <Button className={styles.registerBtn} {...actionButton} />}
                <Icon
                  ariaLabel='chevronRight'
                  icon={'chevronRight'}
                  color='var(--content-primary-tertiary)'
                />
              </div>
            </div>
          )}
          {!actionIcon && actionButton && (
            <Button className={styles.registerBtn} {...actionButton} />
          )}
        </div>

        {!!items?.length && (
          <div className={styles.items}>
            {items.map((item) => {
              return <RegisterSectionItem key={item.id} {...item} />
            })}
          </div>
        )}
      </div>
    )
  }
)
